import { GraphNode } from '../graph-node.js';
import { Observable } from '../observable.js';
import { describeNodeData, traceGraph, traceGraphWarning } from '../debug.js';
import { reportGraphError } from '../errors.js';
export class EventNode extends GraphNode {
    eventImplementation;
    isListeningToInputChanges = false;
    detachCurrentSubscription = null;
    detachInputChangeListeners = [];
    isDisposed = false;
    constructor(id, data, type, eventImplementation = null) {
        super(id, data, type);
        this.eventImplementation = eventImplementation;
    }
    run() {
        if (this.isDisposed)
            return;
        super.run();
        if (!this.isListeningToInputChanges) {
            this.listenToInputChanges();
        }
        this.addEventListeners();
    }
    /** The snapshot taken while subscribing is stale - subscribe again with the new value. */
    refreshAfterUpstreamValueChange() {
        traceGraph(`event ${this.type} (${this.id}) re-subscribing after upstream value change`);
        this.run();
    }
    /** After this the node can never subscribe again, not even on an input change. */
    dispose() {
        this.isDisposed = true;
        traceGraph(`event ${this.type} (${this.id}) disposing`);
        this.stopListeningToInputChanges();
        this.detachPreviousSubscription();
    }
    addEventListeners() {
        if (!this.eventImplementation) {
            traceGraphWarning(`event ${this.type} (${this.id}) has NO implementation - it can never fire`);
            return;
        }
        const triggerOutPortFunctions = this.buildTriggerOutPortFunctions();
        const resolvedData = this.resolveDataForImplementation(this.data);
        traceGraph(`event ${this.type} (${this.id}) subscribing`, {
            outPorts: [...triggerOutPortFunctions.keys()],
            data: describeNodeData(resolvedData),
        });
        this.subscribe(resolvedData, triggerOutPortFunctions);
    }
    /** A failed subscription is silent otherwise: the event can simply never fire. */
    subscribe(resolvedData, triggerOutPortFunctions) {
        if (!this.eventImplementation)
            return;
        this.detachPreviousSubscription();
        try {
            const detach = this.eventImplementation(resolvedData, triggerOutPortFunctions);
            this.detachCurrentSubscription = typeof detach === 'function' ? detach : null;
        }
        catch (error) {
            reportGraphError({
                kind: 'event',
                nodeType: this.type,
                nodeId: this.id,
                summary: 'could not subscribe - this event will never fire',
                error,
            });
        }
    }
    /** Guards the re-subscribe path: a failing detach must not block the new subscription. */
    detachPreviousSubscription() {
        const detach = this.detachCurrentSubscription;
        this.detachCurrentSubscription = null;
        if (!detach)
            return;
        try {
            detach();
        }
        catch (error) {
            reportGraphError({
                kind: 'event',
                nodeType: this.type,
                nodeId: this.id,
                summary: 'could not detach the previous subscription - it may fire twice',
                error,
            });
        }
    }
    buildTriggerOutPortFunctions() {
        const triggers = new Map();
        for (const portKey of this.outConnections.keys()) {
            triggers.set(portKey, () => this.triggerOutPort(portKey));
        }
        return triggers;
    }
    listenToInputChanges() {
        for (const value of Object.values(this.data)) {
            if (value instanceof Observable) {
                const onInputChange = () => {
                    traceGraph(`event ${this.type} (${this.id}) re-subscribing after input change`);
                    this.run();
                };
                value.addOnChangeListener(onInputChange);
                this.detachInputChangeListeners.push(() => value.removeOnChangeListener(onInputChange));
            }
        }
        this.isListeningToInputChanges = true;
    }
    stopListeningToInputChanges() {
        const detachAll = this.detachInputChangeListeners;
        this.detachInputChangeListeners = [];
        for (const detach of detachAll)
            detach();
    }
    resolveDataForImplementation(data) {
        const resolved = {};
        for (const [key, value] of Object.entries(data)) {
            resolved[key] = value instanceof Observable ? value.value : value;
        }
        return resolved;
    }
}
